import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "~/utils/toast";
import { useProduct } from "~/contexts/product-context";
import { Button } from "~/components/button";
import ModalOverlayLoadingSpinner from "~/components/loading/modal-overlay-loading-spinner";
import { useAnalytics } from "~/contexts/analytics-context";
import {
  KeywordsPipelineInfoDialogProvider,
  useKeywordsPipelineInfoDialog,
} from "~/contexts/keywords-pipeline-info-dialog-context";
import { useSession } from "~/contexts/auth-context";
import { ErrorPage } from "~/components/errors/error-page";
import { StyledTabs, StyledTabItem } from "~/components/styled-tabs";
import { MarketingSubtitlesPanel } from "~/components/marketing-subtitles-panel";
import { NotIncludedInExportBadge } from "~/components/not-included-in-export-badge";
import { KeywordsTab, KeywordsTabActions } from "~/components/keywords-tab";
import { getKeywordsByType } from "~/integrations/firebase/collections";
import { saveManuscriptProductAsExcel } from "~/shared/services/export-products-service";
import { useSearchParams } from "~/hooks/use-search-params";
import { ManuscriptDetailsTabBox } from "./components/manuscript-details-tab-box";
import { ManuscriptDetailsHeader } from "./components/manuscript-details-header";
import { ThemaTab } from "./components/thema-tab";
import { DescriptionTab } from "./components/description-tab";
import { AnalysisTab } from "./components/analysis-tab";

export enum MANUSCRIPT_DETAILS_TAB {
  KEYWORDS = "keywords",
  THEMA = "thema",
  DESCRIPTION = "description",
  MARKETING = "marketing",
  ANALYSIS = "analysis",
}

const ManuscriptDetailsContent: React.FC = () => {
  const { t } = useTranslation(["manuscriptDetails", "components"]);
  const navigate = useNavigate();
  const { gaEvent } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    (searchParams.get("tab") as MANUSCRIPT_DETAILS_TAB | null) ??
    MANUSCRIPT_DETAILS_TAB.KEYWORDS;
  const { user } = useSession();
  const { open: openKeywordsPipelineInfoDialog } =
    useKeywordsPipelineInfoDialog();
  const [showReasoning, setShowReasoning] = useState(false);

  const { product, loading, error } = useProduct();

  const { generated: generatedKeywords } = getKeywordsByType(product);
  const hasGeneratedKeywords = generatedKeywords.length > 0;

  const handleShowReasoningChange = (value: boolean) => {
    setShowReasoning(value);
    gaEvent({
      type: "toggle_keyword_reasoning_switch",
      payload: {
        enabled: value,
        book: product?.id,
      },
    });
  };

  const handleGoBack = () => {
    navigate({ to: "/" });
  };

  const handleClick = (tab: MANUSCRIPT_DETAILS_TAB) => {
    gaEvent({
      type: "click",
      payload: { analytics_id: "change_manuscript_tab", text: tab },
    });
  };

  const onTabChange = (tab: MANUSCRIPT_DETAILS_TAB) => {
    searchParams.set("tab", tab);
    setSearchParams(searchParams);
  };

  const handleExcelExportButtonClick = useCallback(async () => {
    try {
      if (!product) {
        return;
      }
      gaEvent({ type: "export_manuscript_book", payload: {} });
      await saveManuscriptProductAsExcel(
        product,
        product.manuscript?.filename || "untitled",
      );
    } catch {
      toast.error(t("manuscriptDetails:buttons.export.error"));
    }
  }, [gaEvent, product, t]);

  if (error) {
    return (
      <ErrorPage
        title={t("manuscriptDetails:errors.general.title")}
        message={t("manuscriptDetails:errors.general.message")}
        error={error}
      />
    );
  }

  if (!product && !loading) {
    return (
      <ErrorPage
        title={t("manuscriptDetails:errors.notFound.title")}
        message={t("manuscriptDetails:errors.notFound.message")}
      />
    );
  }

  const tabs: StyledTabItem<MANUSCRIPT_DETAILS_TAB>[] = [
    {
      value: MANUSCRIPT_DETAILS_TAB.KEYWORDS,
      label: t("manuscriptDetails:tabs.keywords"),
      onClick: () => handleClick(MANUSCRIPT_DETAILS_TAB.KEYWORDS),
      action: hasGeneratedKeywords ? (
        <KeywordsTabActions
          showReasoning={showReasoning}
          onShowReasoningChange={handleShowReasoningChange}
          onPipelineInfoOpen={openKeywordsPipelineInfoDialog}
        />
      ) : undefined,
    },
    {
      value: MANUSCRIPT_DETAILS_TAB.THEMA,
      label: t("manuscriptDetails:tabs.thema"),
      onClick: () => handleClick(MANUSCRIPT_DETAILS_TAB.THEMA),
      testId: "thema-tab",
    },
    {
      value: MANUSCRIPT_DETAILS_TAB.DESCRIPTION,
      label: t("manuscriptDetails:tabs.description"),
      onClick: () => handleClick(MANUSCRIPT_DETAILS_TAB.DESCRIPTION),
      testId: "description-tab",
    },
    {
      value: MANUSCRIPT_DETAILS_TAB.MARKETING,
      label: t("manuscriptDetails:tabs.marketing"),
      onClick: () => handleClick(MANUSCRIPT_DETAILS_TAB.MARKETING),
      testId: "marketing-tab",
      action: <NotIncludedInExportBadge />,
    },
  ];

  // Add analysis tab if user is super admin
  if (user.isSuperAdmin) {
    tabs.push({
      value: MANUSCRIPT_DETAILS_TAB.ANALYSIS,
      label: t("manuscriptDetails:tabs.analysis"),
      onClick: () => handleClick(MANUSCRIPT_DETAILS_TAB.ANALYSIS),
      testId: "analysis-tab",
    });
  }

  return (
    <div className="min-w-[410px]">
      <Button
        variant="ghost"
        label={t("manuscriptDetails:header.back")}
        icon="arrow-left"
        className="-ml-4"
        onClick={handleGoBack}
        analyticsId="button_back_to_home"
        data-testid="home-link"
      />

      <ModalOverlayLoadingSpinner isLoading={loading} />

      <div className="w-full flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <ManuscriptDetailsHeader
            filename={product?.manuscript?.filename ?? ""}
            onExportClick={handleExcelExportButtonClick}
          />
        </div>

        <StyledTabs activeTab={activeTab} onTabChange={onTabChange} tabs={tabs}>
          <ManuscriptDetailsTabBox
            value="keywords"
            className="max-h-(--subject-list-container-height) h-screen"
          >
            <KeywordsTab showReasoning={showReasoning} />
          </ManuscriptDetailsTabBox>
          <ManuscriptDetailsTabBox value="thema">
            <ThemaTab />
          </ManuscriptDetailsTabBox>
          <ManuscriptDetailsTabBox value="description">
            <DescriptionTab />
          </ManuscriptDetailsTabBox>
          <ManuscriptDetailsTabBox value="marketing">
            <MarketingSubtitlesPanel />
          </ManuscriptDetailsTabBox>
          {user.isSuperAdmin && (
            <ManuscriptDetailsTabBox value="analysis">
              <AnalysisTab />
            </ManuscriptDetailsTabBox>
          )}
        </StyledTabs>
      </div>
    </div>
  );
};

export const ManuscriptDetails: React.FC = () => {
  return (
    <KeywordsPipelineInfoDialogProvider autoOpen={false}>
      <ManuscriptDetailsContent />
    </KeywordsPipelineInfoDialogProvider>
  );
};
